import React, { useCallback, useRef, useState } from "react";

// Services
import ApiService from "../../services/ApiService";

// Components
import {
  CheckboxCustomEvent,
  IonAlert,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonCol,
  IonContent,
  IonFooter,
  IonHeader,
  IonItem,
  IonLabel,
  IonLoading,
  IonModal,
  IonRow,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { FormComponent, FormComponentRef } from "../../components/Forms/FormComponent";
import BasicTemplate from "../../components/PageTemplates/BasicTemplate";

// Styles
import { TrixContent } from "../../components/TrixContent";
import "./Register.scss";

// Services
import ReactGA from "react-ga4";
import AuthService from "../../services/AuthService";
import { MemberInterface } from "../../types";
import { useViewport } from "../../utils/useViewport";

const Register = (props: any) => {
  const [eula, setEula] = useState<string>("");
  const [eulaIsOpen, setEulaIsOpen] = useState<boolean>(false);
  const [eulaIsAccepted, setEulaIsAccepted] = useState<boolean>(false);
  const registerForm = useRef<FormComponentRef>(null);
  const { isDesktop } = useViewport();

  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>(t("Loading") || "");
  const [present] = useIonToast();
  const [errorAlertIsOpen, setErrorAlertIsOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const openEula = () => {
    setEulaIsOpen(true);
    ApiService.get("manifest/eula").then(({ eula }) => {
      setEula(eula);
    });
  };

  const handleSubmit = useCallback(() => {
    registerForm.current?.submit();
  }, [registerForm]);

  return (
    <BasicTemplate className={`page--register register`} hideBackButton={false} hideHeader={false} title={t("register") || ""}>
      <IonContent className={"ion-padding"}>
        <IonHeader collapse={"condense"}>
          <IonToolbar>
            <IonTitle slot={"start"} size={"large"}>
              {t("register")}
            </IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonAlert isOpen={errorAlertIsOpen} message={errorMessage} header={t("error") || ""} onDidDismiss={() => setErrorAlertIsOpen(false)} buttons={["OK"]} />

        {props.match.params.name && (
          <p>
            {t("youAreRegisteringAsA")} {props.match.params.name}.
          </p>
        )}

        <IonRow>
          <IonCol size="12" sizeMd="6">
            <div className="register__container">
              <IonLoading isOpen={loading} message={loadingMessage} />
              <FormComponent
                ref={registerForm}
                name={`register`}
                queryParameters={{
                  memberTypeId: props.match.params.id,
                }}
                includeLabels={true}
                onSubmitting={() => {
                  setLoading(true);
                  setLoadingMessage(t("submitting") || "");
                }}
                onSuccess={(member: MemberInterface, formData: any) => {
                  setLoading(false);

                  if (member.api_token) {
                    ApiService.setApiToken(member.api_token);
                  } else {
                    console.error("No API token was found in API response.");
                  }

                  ReactGA.event({
                    category: "register",
                    action: "complete",
                    label: t("instance") || "",
                  });

                  // Now log the user straight in
                  AuthService.login(formData.email, formData.password)
                    .then((member) => {
                      // they'll get redirected to / in the finally block
                      present({
                        message: t("registrationSuccessLoggedIn") || "",
                        duration: 3000,
                        position: "bottom",
                      });
                    })
                    .catch((err) => {
                      // they were registered but can't be logged in, so show a toast instead and let them log in later
                      present({
                        message: t("registrationSuccessLoginRequired") || "",
                        duration: 3000,
                        position: "bottom",
                      });
                    })
                    .finally(() => {
                      // redirect to home page once either logged in or toast displayed
                      props.history.push(`/`);
                    });
                }}
                onError={(err) => {
                  // if it's a Member Type issue, show an alert
                  if (Object.keys(err.errors).length === 1 && err.errors.hasOwnProperty("memberTypeId")) {
                    setErrorMessage(err.errors.memberTypeId[0]);
                    setErrorAlertIsOpen(true);
                  } else {
                    console.error(err);
                  }

                  setLoading(false);
                }}
              />

              <IonItem
                style={{
                  "--padding-start": 0,
                  "--border-width": 0,
                  "--inner-border-width": 0,
                  marginTop: "1em",
                }}
              >
                <IonCheckbox
                  slot={"start"}
                  checked={eulaIsAccepted}
                  onIonChange={(e: CheckboxCustomEvent) => {
                    setEulaIsAccepted(e.target.checked);
                  }}
                ></IonCheckbox>
                <IonLabel>
                  I have read and accept the {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a
                    href={"#"}
                    onClick={(e) => {
                      e.preventDefault();
                      openEula();
                      return false;
                    }}
                    id={"open-eula"}
                  >
                    EULA
                  </a>
                </IonLabel>
              </IonItem>

              {isDesktop && (
                <IonButton disabled={!eulaIsAccepted} onClick={handleSubmit} type="submit" className="ion-no-margin">
                  {t("register")}
                </IonButton>
              )}
            </div>
          </IonCol>
        </IonRow>
      </IonContent>

      {!isDesktop && (
        <IonFooter
          style={{
            border: "none",
          }}
        >
          <IonToolbar>
            <IonButton
              disabled={!eulaIsAccepted}
              onClick={handleSubmit}
              expand="block"
              type="submit"
              className="ion-no-margin"
              style={{
                textTransform: "capitalize",
              }}
            >
              {t("register")}
            </IonButton>
          </IonToolbar>
        </IonFooter>
      )}

      <IonModal isOpen={eulaIsOpen}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>EULA</IonTitle>
            <IonButtons slot={"end"}>
              <IonButton onClick={() => setEulaIsOpen(false)}>Close</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className={"ion-padding"}>
          <TrixContent content={eula} />
        </IonContent>
      </IonModal>
    </BasicTemplate>
  );
};

export default Register;
